import React, { createContext, useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { ArrowUpOutlined, DownOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Col, Drawer, Dropdown, List, Menu, Pagination, Row } from "antd";
import * as style from "./style.less";
import { useSearchForRheses } from "../../api/api";
import { usePageNavigation } from "../Page/NavigationProvider";
import { SubToPages_pages } from "../../api/SubToPages";
import { useI18nObjectHook } from "../../../../../i18n";
import { RheseSearchMode } from "../../../../../../__generated__/globalTypes";
import { getMenuItem, MenuItem } from "../../../styles/antDesignTheme";
import { SearchForRheses_searchForRheses_results } from "../../api/__generated__/SearchForRheses";

const SEARCH_MODES: { [key: string]: SearchModeType } = {
  STRING: "string",
  ID: "id",
};

type SearchModeType = "string" | "id";

type SearchModeLabelTranslateProps = { id: string; text: string };

type nbResultTranslateProps = {
  loading: string;
  result: string;
  results: string;
};

type SearchState = {
  isOpen: boolean;
  searchString: string;
  searchMode: SearchModeType;
  results: Result[];
};

type SearchCommand =
  | {
      id: "SET_SEARCH_STRING";
      payload: string;
    }
  | {
      id: "TOGGLE";
    }
  | {
      id: "SET_SEARCH_MODE";
      payload: "string" | "id";
    }
  | {
      id: "OPEN";
    }
  | {
      id: "CLOSE";
    };

type Result = SearchForRheses_searchForRheses_results;

const DEFAULT_SEARCH: SearchState = {
  isOpen: false,
  searchString: "",
  searchMode: "string",
  results: [],
};

export const SearchContext = createContext(DEFAULT_SEARCH);

export function useSearchReducer(bookId: string) {
  const [search, dispatch] = useReducer(
    (state: SearchState, command: SearchCommand): SearchState => {
      switch (command.id) {
        case "SET_SEARCH_STRING":
          return { ...state, searchString: command.payload };
        case "SET_SEARCH_MODE":
          return { ...state, searchMode: command.payload };
        case "OPEN":
          return { ...state, isOpen: true };
        case "CLOSE":
          return { ...state, isOpen: false };
        case "TOGGLE":
          return { ...state, isOpen: !state.isOpen };
        default:
          return state;
      }
    },
    DEFAULT_SEARCH,
  );
  const [query, setQuery] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.searchString), 300);
    return () => clearTimeout(timer);
  }, [search.searchString]);
  const { data, loading } = useSearchForRheses({
    query,
    mode: search.searchMode === "id" ? RheseSearchMode.ID : RheseSearchMode.CONTENT,
    projectId: bookId,
  });
  const results = useMemo(
    () => [...(data?.searchForRheses?.results || [])].sort((a, b) => a.pageNumber - b.pageNumber),
    [data],
  );
  const send = useMemo(
    () => ({
      setSearchMode: (payload: SearchModeType) => dispatch({ id: "SET_SEARCH_MODE", payload }),
      setSearchString: (payload: string) => dispatch({ id: "SET_SEARCH_STRING", payload }),
      toggle: () => dispatch({ id: "TOGGLE" }),
      open: () => dispatch({ id: "OPEN" }),
      close: () => dispatch({ id: "CLOSE" }),
    }),
    [],
  );
  const state = useMemo(
    () => ({ ...search, results: search.isOpen ? results : DEFAULT_SEARCH.results }),
    [search, results],
  );
  return { state, send, results, loading, total: data?.searchForRheses.totalMatchNumber ?? 0 };
}

type SearchProps = {
  pages: SubToPages_pages[];
  isOpen: boolean;
  close: () => void;
  setSearchMode: (mode: SearchModeType) => void;
  results: Result[];
  loading: boolean;
  totalElements: number;
  textQuery: string;
  setTextQuery: (value: string) => void;
  searchMode: SearchModeType;
  bookId: string;
};

const getSearchModeLabel = (
  searchMode: SearchModeType,
  translation: SearchModeLabelTranslateProps,
) => {
  if (searchMode === "id") return translation.id;
  return translation.text;
};

export default function Search({
  isOpen,
  close,
  setSearchMode,
  searchMode,
  results,
  loading,
  totalElements,
  textQuery,
  setTextQuery,
}: SearchProps) {
  const [page, setPage] = useState(1);
  const [inputRef, setInputRef] = useState<HTMLInputElement | null>(null);
  const { requestPage } = usePageNavigation();
  const getSearchModeLabelTranslate: SearchModeLabelTranslateProps = useI18nObjectHook(
    "project.search.getSearchModeLabel",
  );

  const nbResultTranslate: nbResultTranslateProps = useI18nObjectHook("project.search.nbResults");

  useEffect(() => inputRef?.focus(), [inputRef]);

  const searchModeLabel = useMemo(
    () => getSearchModeLabel(searchMode, getSearchModeLabelTranslate),
    [searchMode, getSearchModeLabelTranslate],
  );

  const updateQuery = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setTextQuery(e.target.value);
      setPage(1);
    },
    [setTextQuery],
  );

  const renderDropdownContent = useCallback(() => {
    const items: MenuItem[] = [
      getMenuItem({
        key: "1",
        label: getSearchModeLabel(SEARCH_MODES.STRING, getSearchModeLabelTranslate),
        onClick: () => setSearchMode("string"),
      }),
      getMenuItem({
        key: "2",
        label: getSearchModeLabel(SEARCH_MODES.ID, getSearchModeLabelTranslate),
        onClick: () => setSearchMode("id"),
      }),
    ];

    return <Menu className={style.dropDownMenu} items={items} />;
  }, [getSearchModeLabelTranslate, setSearchMode]);

  return (
    <Drawer
      title="Recherche"
      placement="right"
      styles={{ body: { padding: 0, display: "flex", flexDirection: "column" } }}
      className={style.root}
      open={isOpen}
      closable={true}
      onClose={() => {
        close();
      }}
      mask={false}
      width="35em">
      <List
        style={{ flex: 1, overflowY: "auto" }}
        size="large"
        dataSource={results.slice((page - 1) * 20, page * 20)}
        loading={loading}
        header={
          <Row className={style.search} justify={"center"} align={"middle"}>
            <Col style={{ textAlign: "center", fontSize: "1.2em" }} span={2}>
              <SearchOutlined />
            </Col>
            <Col span={16}>
              <input ref={setInputRef} value={textQuery} onChange={updateQuery} />
            </Col>
            <Col span={6}>
              <Dropdown dropdownRender={renderDropdownContent}>
                <Button>
                  {searchModeLabel} <DownOutlined />
                </Button>
              </Dropdown>
            </Col>
          </Row>
        }
        renderItem={(result) => {
          return (
            <List.Item
              onClick={() => {
                requestPage(result.pageNumber);
              }}
              className={style.result}>
              <span>{result.pageNumber}</span>
              {result.content}
              <span>
                <ArrowUpOutlined className={style.arrow} />
              </span>
            </List.Item>
          );
        }}
      />
      <Pagination
        className={style.pagination}
        onChange={setPage}
        current={page}
        total={totalElements}
        pageSize={20}
        hideOnSinglePage
        responsive
        showSizeChanger={false}
        size="small"
      />
      <p className={style.nbResults}>
        {loading
          ? nbResultTranslate.loading
          : `${results.length} ${
              results.length > 1 ? nbResultTranslate.results : nbResultTranslate.result
            }`}
      </p>
    </Drawer>
  );
}
